import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';

import { useI18n } from '#hooks/useI18n';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Calendar, Regular, SelectHeader, Sparkles, Spinner, Tooltip } from '@zazume/zzm-base';
import { DateVO } from '@zazume/zzm-utils';

import { ErrorType } from '../../utils/formsHelper';
import {
  CalendarComponent,
  CalendarPositions,
  IntervalTimePickerType,
  SelectableHours,
  Weekday
} from './CalendarComponent';
import { isDateSelectable, parseDateInput, toSafeDate } from './calendarHelper';

interface ContainerProps {
  maxWidth?: string;
  width?: string;
}

const Container = styled.div<ContainerProps>`
  ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth};`}
  width: ${({ width }) => width ?? '100%'};
`;

const InputWrapper = styled.div<{ errors?: ErrorType; disabled?: boolean }>(
  ({ theme, errors, disabled }) => css`
    background: ${disabled ? 'none' : theme.colors.White};
    box-sizing: border-box;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    border-radius: ${theme.radius.lg};
    min-width: 5.375rem;
    height: ${theme.spacing.s14};
    padding-left: ${theme.spacing.s6};
    display: flex;
    align-items: center;
    flex-grow: 1;
    border: ${theme.borderWeights.light} solid
      ${errors ? theme.colors.Red : disabled ? theme.colors.Gray200 : theme.colors.Gray300};

    &:hover {
      border: ${theme.borderWeights.light} solid ${disabled ? theme.colors.Gray200 : theme.colors.Primary};
    }
  `
);

const DateInput = styled.input<{ hasValue: boolean }>(
  ({ theme, hasValue }) => css`
    flex-grow: 1;
    border: none;
    background: none;
    outline: none;
    font-family: ${theme.fonts.base};
    font-size: ${theme.fontsSizes.base};
    color: ${hasValue ? theme.colors.Gray600 : theme.colors.Gray400};
    padding: 0 4px;
    min-width: 0;

    &::placeholder {
      color: ${theme.colors.Gray400};
    }

    &:disabled {
      cursor: not-allowed;
    }
  `
);

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SparklesButton = styled.button(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${theme.colors.White};
    border: ${theme.borderWeights.light} solid ${theme.colors.Gray300};
    border-radius: ${theme.radius.lg};
    height: ${theme.spacing.s14};
    width: ${theme.spacing.s14};
    padding: 0;
    cursor: pointer;
    flex-shrink: 0;
    transition: border-color 0.15s;

    &:hover:not(:disabled) {
      border-color: ${theme.colors.Primary};
    }

    &:disabled {
      cursor: not-allowed;
    }
  `
);

const CalendarWrapper = styled.div`
  position: relative;
`;

const HeaderContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Label = styled.label(
  ({ theme }) => css`
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 28px;
    color: ${theme.colors.Gray600};
    margin: 0;
    height: 28px;
  `
);

const Helper = styled.div(
  ({ theme }) => css`
    margin: ${theme.spacing.s1} 0;
  `
);

const Error = styled.div(
  ({ theme }) => css`
    color: ${theme.colors.Red};
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 16px;
    margin: 2px 0;
  `
);

export interface DatePickerProps {
  register?: any;
  setValue?: any;
  getValue?: any;
  setError?: any;
  clearErrors?: any;
  name: string;
  label?: string;
  help?: string;
  width?: string;
  maxWidth?: string;
  confirmButton?: string;
  defaultDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  errors?: ErrorType;
  placeholder?: string;
  selectHour?: boolean;
  startingHour?: SelectableHours;
  totalSelectableHours?: SelectableHours;
  interval?: IntervalTimePickerType;
  position?: CalendarPositions;
  autoSave?: boolean;
  onSelected?: (date: Date | null) => void;
  disabledDays?: Weekday[];
  onRemove?: () => void;
  closeOnSelect?: boolean;
  isOptional?: boolean;
  timezone?: string;
  showErrorsAlways?: boolean;
  proposalValue?: Date;
  proposalTooltip?: string;
  withTextInput?: boolean;
}

/**
 * @deprecated use src/components/calendar/DatePickerBase.tsx
 */
export const DatePicker: FC<DatePickerProps> = ({
  register,
  setValue,
  getValue,
  setError,
  clearErrors,
  name,
  label,
  maxWidth,
  width,
  confirmButton,
  defaultDate,
  minDate,
  maxDate,
  disabled = false,
  errors,
  placeholder = 'dd/mm/yyyy',
  selectHour = false,
  startingHour,
  totalSelectableHours,
  interval,
  position,
  autoSave = true,
  onSelected,
  disabledDays,
  onRemove,
  help,
  closeOnSelect = true,
  isOptional,
  timezone,
  showErrorsAlways = false,
  proposalValue,
  proposalTooltip,
  withTextInput = false
}) => {
  const { t, languageVO } = useI18n();

  const rawDefaultValue = defaultDate ?? getValue?.(name) ?? null;
  const rawDefaultValueTime = rawDefaultValue instanceof Date ? rawDefaultValue.getTime() : null;
  const initialValue = toSafeDate(rawDefaultValue);
  const formatPattern = selectHour ? 'datetimeMinutes' : 'date';

  const formatDate = useCallback(
    (date: Date) => {
      return DateVO.fromPrimitive(date).format(formatPattern, languageVO.toPlatform());
    },
    [languageVO, formatPattern]
  );

  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [isProposalLoading, setIsProposalLoading] = useState(false);
  const [date, setDate] = useState<Date | null>(initialValue);
  const [inputError, setInputError] = useState<string | null>(null);
  const [inputText, setInputText] = useState(initialValue ? formatDate(initialValue) : '');

  useEffect(() => {
    const safeDate = toSafeDate(rawDefaultValue);
    setDate(safeDate);
    setInputText(safeDate ? formatDate(safeDate) : '');
    setValue?.(name, safeDate ?? undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawDefaultValueTime]);

  const setValidationError = useCallback(
    (errorMsg: string | null) => {
      setInputError(errorMsg);
      if (errorMsg) {
        setError?.(name, { type: 'manual', message: errorMsg });
      } else {
        clearErrors?.(name);
      }
    },
    [setError, clearErrors, name]
  );

  const toggleCalendar = useCallback(() => {
    setIsCalendarVisible(prev => !prev);
  }, []);

  const onSelectDateFromCalendar = useCallback(
    (newDate: Date) => {
      setDate(newDate);
      setValue?.(name, newDate, { shouldDirty: true });
      setValidationError(null);
      setInputText(formatDate(newDate));
      onSelected?.(newDate);

      if (closeOnSelect) {
        toggleCalendar();
      }
    },
    [closeOnSelect, name, onSelected, setValue, toggleCalendar, formatDate, setValidationError]
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 8);

      let formatted = digits;
      if (digits.length > 4) {
        formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
      } else if (digits.length > 2) {
        formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
      }

      setInputText(formatted);

      if (digits.length === 8) {
        const parsed = parseDateInput(formatted);
        if (parsed && isDateSelectable(parsed, minDate, maxDate, disabledDays)) {
          setValidationError(null);
          setDate(parsed);
          setValue?.(name, parsed, { shouldDirty: true });
          onSelected?.(parsed);
        }
      }
    },
    [disabledDays, maxDate, minDate, name, onSelected, setValue, setValidationError]
  );

  const handleInputBlur = useCallback(() => {
    if (!inputText.trim()) {
      setValidationError(null);
      setDate(null);
      setValue?.(name, undefined, { shouldDirty: true });
      onSelected?.(null);
      return;
    }

    const parsed = parseDateInput(inputText);
    if (parsed && isDateSelectable(parsed, minDate, maxDate, disabledDays)) {
      setValidationError(null);
      setDate(parsed);
      setValue?.(name, parsed, { shouldDirty: true });
      onSelected?.(parsed);
      setInputText(formatDate(parsed));
    } else {
      setValidationError(t('general.forms.invalidDate'));
    }
  }, [disabledDays, inputText, maxDate, minDate, name, onSelected, setValue, t, formatDate, setValidationError]);

  const handleProposalClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!proposalValue || isProposalLoading) {
        return;
      }
      setIsProposalLoading(true);
      setTimeout(() => {
        setDate(proposalValue);
        setValue?.(name, proposalValue, { shouldDirty: true });
        setValidationError(null);
        setInputText(formatDate(proposalValue));
        onSelected?.(proposalValue);
        setIsProposalLoading(false);
      }, 300);
    },
    [proposalValue, isProposalLoading, name, setValue, formatDate, onSelected, setValidationError]
  );

  const snoozeTime = date?.getTime() ?? new DateVO().getTime();

  const headerText = date ? DateVO.fromPrimitive(date).format(formatPattern, languageVO.toPlatform()) : undefined;

  const errorState = errors || (inputError ? ({ message: inputError } as ErrorType) : undefined);
  const hasError = Boolean(errorState);

  const renderHeaderContent = () => {
    if (selectHour || !withTextInput) {
      return (
        <SelectHeader
          hasError={hasError}
          headerIcon="calendar"
          onClick={disabled ? undefined : toggleCalendar}
          selectedOptionName={headerText}
          placeholder={placeholder}
          disabled={disabled}
        />
      );
    }

    return (
      <InputWrapper errors={errorState} disabled={disabled} onClick={disabled ? undefined : toggleCalendar}>
        <Calendar color={date ? 'Gray500' : 'Gray400'} scale="sm" />
        <DateInput
          hasValue={Boolean(date)}
          value={inputText}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          autoComplete="off"
        />
      </InputWrapper>
    );
  };

  return (
    <Container maxWidth={maxWidth} width={width}>
      <HeaderContainer>
        {label && <Label>{label}</Label>}
        {isOptional && (
          <Regular variant="S" color="Gray400">
            {t('general.optional')}
          </Regular>
        )}
      </HeaderContainer>
      <CalendarWrapper>
        <InputRow>
          {renderHeaderContent()}
          {proposalValue && !disabled && (
            <Tooltip longTextAvailable name="proposal-tooltip" text={proposalTooltip || t('general.autoFillWithAI')}>
              <SparklesButton type="button" onClick={handleProposalClick} disabled={isProposalLoading}>
                {isProposalLoading ? (
                  <Spinner size="extra-small" color="Primary" />
                ) : (
                  <Sparkles scale="sm" color="Primary" />
                )}
              </SparklesButton>
            </Tooltip>
          )}
        </InputRow>
        <CalendarComponent
          key={date?.getTime() ?? 0}
          position={position}
          snooze={snoozeTime}
          onSelect={onSelectDateFromCalendar}
          isVisible={isCalendarVisible}
          hide={toggleCalendar}
          selectHour={selectHour}
          name={name}
          register={register}
          setValue={setValue}
          confirmationButtonText={confirmButton}
          minDate={minDate}
          maxDate={maxDate}
          startingHour={startingHour}
          totalSelectableHours={totalSelectableHours}
          interval={interval}
          autoSave={autoSave}
          disabledDays={disabledDays}
          onRemove={onRemove}
          timezone={timezone}
        />
      </CalendarWrapper>
      {help && (
        <Helper>
          <Regular variant="XS" color="Gray400">
            {help}
          </Regular>
        </Helper>
      )}
      {errorState && (!disabled || showErrorsAlways) && <Error>{errorState.message}</Error>}
    </Container>
  );
};
